
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from "@/components/ui/form";
import { Input } from '@/components/ui/input';
import { getRecaptchaScriptUrl } from '@/utils/configService';
import { sendContactForm } from '@/utils/contactFormService';
import { calculatorContactFormSchema, CalculatorContactFormValues } from './ContactFormSchema';

// Import our shared form components
import { FormField, PrivacyDisclaimer } from '@/components/contact/form';

interface ContactFormProps {
  onSubmit: (values: CalculatorContactFormValues) => void;
  onCancel: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSubmit, onCancel }) => {
  const form = useForm<CalculatorContactFormValues>({
    resolver: zodResolver(calculatorContactFormSchema),
    defaultValues: {
      naam: "",
      bedrijf: "",
      email: "",
      telefoonnummer: "",
    },
  });

  // Load reCAPTCHA script when component mounts
  useEffect(() => {
    const script = document.createElement('script');
    script.src = getRecaptchaScriptUrl();
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    
    return () => {
      // Cleanup: remove script when component unmounts
      const scriptElement = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, []);

  const handleSubmit = async (values: CalculatorContactFormValues) => {
    try {
      // First send the data to our contact form service
      await sendContactForm(
        values,
        "Nieuwe offerte aanvraag vanuit calculator",
        { source: "Calculator pagina" }
      );
      
      // Track the form submission as a conversion
      const { trackConversion } = await import('@/utils/analyticsService');
      trackConversion();
      
      // Then call the onSubmit prop
      onSubmit(values);
    } catch (error) {
      console.error("Error sending calculator contact form:", error);
      // Still call onSubmit to maintain existing behavior
      onSubmit(values);
    }
  };

  return (
    <section className="section bg-brand-gray" id="contact-form-section">
      <div className="container">
        <div className="max-w-xl mx-auto">
          <div className="bg-white p-6 md:p-8 rounded-lg shadow-md border border-gray-200">
            <h3 className="font-semibold text-2xl mb-6 text-gray-900">Vraag nu vrijblijvend jouw offerte aan</h3>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-6">
                <FormField
                  form={form}
                  name="naam"
                  label="Naam*"
                >
                  <Input placeholder="Jouw naam" />
                </FormField>
                
                <FormField
                  form={form}
                  name="bedrijf"
                  label="Bedrijf*"
                >
                  <Input placeholder="Jouw bedrijfsnaam" />
                </FormField>
                
                <FormField
                  form={form}
                  name="email"
                  label="E-mail*"
                >
                  <Input type="email" placeholder="jouw@email.be" />
                </FormField>
                
                <FormField
                  form={form}
                  name="telefoonnummer"
                  label="Telefoonnummer*"
                >
                  <Input type="tel" placeholder="+32 ..." />
                </FormField>
                
                <div className="flex gap-4 mt-2">
                  <button type="submit" className="btn-primary flex-1 h-12 flex items-center justify-center">
                    Offerte aanvragen
                  </button>
                  <button 
                    type="button" 
                    onClick={onCancel} 
                    className="btn-outline h-12 flex items-center justify-center"
                  >
                    Annuleren
                  </button>
                </div>
                
                <div className="text-xs text-gray-500 mt-2">
                  Deze site is beschermd door reCAPTCHA en de Google 
                  <a href="https://policies.google.com/privacy" className="text-brand-blue hover:underline" target="_blank" rel="noopener noreferrer"> Privacybeleid</a> en
                  <a href="https://policies.google.com/terms" className="text-brand-blue hover:underline" target="_blank" rel="noopener noreferrer"> Servicevoorwaarden</a> zijn van toepassing.
                </div>
                
                <PrivacyDisclaimer />
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
